import React, { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const LeadNotesEditor = ({ leadId, initialNotes = '', onSave }) => {
  const [editorState, setEditorState] = useState(() => {
    if (initialNotes) {
      try {
        const blocksFromHTML = convertFromHTML(initialNotes);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
      } catch (e) {
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  });
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [errorMessage, setErrorMessage] = useState('');
  const saveTimeoutRef = useRef(null);
  const editorRef = useRef(null);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Prevent auto-focus and scrolling on mount
  useEffect(() => {
    // Prevent the editor from auto-focusing on mount
    if (editorRef.current) {
      const editorDiv = editorRef.current.querySelector('.public-DraftEditor-content');
      if (editorDiv) {
        editorDiv.setAttribute('contenteditable', 'true');
        editorDiv.blur(); // Ensure no focus
      }
    }
  }, []);

  // Update editor state when initialNotes prop changes
  useEffect(() => {
    if (initialNotes) {
      try {
        const blocksFromHTML = convertFromHTML(initialNotes);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
      } catch (e) {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [initialNotes]);

  // Auto-save with debounce
  useEffect(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Convert editor state to HTML
    const currentContent = editorState.getCurrentContent();
    const htmlContent = draftToHtml(convertToRaw(currentContent));

    // Don't auto-save on initial mount or if notes unchanged
    if (htmlContent === initialNotes) {
      return;
    }

    // Set saving status
    setSaveStatus('saving');

    // Debounce save by 2 seconds
    saveTimeoutRef.current = setTimeout(() => {
      handleSave(htmlContent);
    }, 2000);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [editorState]);

  const handleSave = async (notesContent) => {
    try {
      const response = await fetch(`${REACT_APP_BACKEND_URL}/api/leads/${leadId}/notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ lead_notes: notesContent })
      });

      if (!response.ok) {
        throw new Error('Failed to save notes');
      }

      setSaveStatus('saved');
      setErrorMessage('');
      
      // Call onSave callback if provided
      if (onSave) {
        onSave(notesContent);
      }

      // Reset to idle after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);

    } catch (error) {
      console.error('Error saving lead notes:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to save notes. Please try again.');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 3000);
    }
  };

  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
  };

  // Toolbar configuration
  const toolbarOptions = {
    options: ['inline', 'blockType', 'list', 'link', 'colorPicker'],
    inline: {
      options: ['bold', 'italic', 'underline']
    },
    blockType: {
      inDropdown: true,
      options: ['Normal', 'H1', 'H2', 'H3']
    },
    list: {
      options: ['unordered', 'ordered']
    },
    link: {
      options: ['link']
    },
    colorPicker: {
      colors: ['rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
        'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(65,168,95)', 'rgb(0,168,133)',
        'rgb(61,142,185)', 'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(0,0,0)',
        'rgb(247,218,100)', 'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
        'rgb(239,239,239)', 'rgb(255,255,255)', 'rgb(250,197,28)', 'rgb(243,121,52)', 'rgb(209,72,65)',
        'rgb(184,49,47)', 'rgb(124,112,107)', 'rgb(209,213,216)']
    }
  };

  return (
    <Card className="mb-6 border-blue-200 bg-blue-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-900">Lead Notes</CardTitle>
            <CardDescription className="text-sm text-gray-600 mt-1">
              Your personal notes about this lead
            </CardDescription>
          </div>
          
          {/* Save Status Indicator */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <div className="flex items-center gap-1 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Check className="h-4 w-4" />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span>Error</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Draft.js Editor */}
        <div ref={editorRef} className="bg-white rounded-lg border border-gray-300 lead-notes-editor">
          <Editor
            editorState={editorState}
            onEditorStateChange={onEditorStateChange}
            toolbar={toolbarOptions}
            placeholder="Write your notes about this lead here... (supports rich text formatting)"
            editorClassName="lead-notes-editor-content"
            toolbarClassName="lead-notes-editor-toolbar"
            spellCheck={true}
            readOnly={false}
            tabIndex={-1}
            stripPastedStyles={false}
          />
        </div>
        
        {/* Info Note */}
        <div className="mt-3 flex items-start gap-2 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-2">
          <AlertCircle className="h-3 w-3 text-blue-600 mt-0.5 flex-shrink-0" />
          <p>
            <strong>Note:</strong> This section is for your personal notes and will not be edited by AI. 
            Changes are automatically saved as you type. Your notes will be used as context to improve AI enrichment.
          </p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
            {errorMessage}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadNotesEditor;
